import { ImmutableMap, Nullable } from 'models/helpers'
import { Orientations, Styles } from 'constants/floorPlan'
import { ProductKind, ProductKindsFloorPlan } from 'constants/product'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { usePurchaseFlowConfig, usePurchaseFlowProducts, usePurchaseFlowRealEstateProperty, useTargetOrderUser } from 'components/pages/PurchaseFlow/_main/contexts'

import { FloorPlanColors } from 'constants/floorPlan'
import { FloorPlanConfigDTO } from 'models/purchaseFlow'
import { FloorPlanFormat } from '../FloorPlanLayout'
import { Language } from 'translations/Language'
import constate from 'constate'
import { findExtraProductByParent } from 'utils/purchaseFlow'
import { isEditingCategory } from 'utils/validators'

import { FloorPlanFileOrientation, FloorPlanProductSettings, FloorPlanProductType, FloorPlanRenderType, FloorPlanSelectionType } from 'constants/floorPlan'

export { FloorPlanFileOrientation, FloorPlanProductType, FloorPlanRenderType, FloorPlanSelectionType }
export type { FloorPlanProductSettings }

const kindsWithDisallowed3D = new Set([ProductKind.MATTERPORT_FLOOR_PLAN_WITH_VISIT])

export const [FloorPlanConfigProvider, useFloorPlanConfig] = constate(() => {
  const { selectedCategory, extraCatalogueProducts, toCatalogueOptionProduct } = usePurchaseFlowConfig()
  const { targetUser } = useTargetOrderUser()
  const {
    hasFloorPlanProduct,
    selectedProducts,
    selectOptionProduct,
    selectedProductOptions,
    unselectOptionProduct,
    selectedKinds,
  } = usePurchaseFlowProducts()
  const { selectedPropertyType, setSelectedPropertyType } = usePurchaseFlowRealEstateProperty()

  const [currentFloorPlanRenderType, setCurrentFloorPlanRenderType] = useState<FloorPlanRenderType>(FloorPlanRenderType.RENDER_2D)

  const currentFloorPlanProductType: FloorPlanProductType = useMemo(() => {
    if (selectedCategory && isEditingCategory(selectedCategory)) return FloorPlanProductType.PRODUCT_EDITING
    return FloorPlanProductType.PRODUCT_BASIC
  }, [selectedCategory])

  const is3DAllowed = useMemo(() => !Array.from(selectedKinds).some(kind => kindsWithDisallowed3D.has(kind)), [selectedKinds])

  // PRODUCT SETTINGS
  const [floorPlanProductSettings, setFloorPlanProductSettings] = useState<ImmutableMap<number, FloorPlanProductSettings>>(ImmutableMap())

  // DESIGN
  const [selectedColor, setSelectedColor] = useState<FloorPlanColors>(FloorPlanColors.BLACK)
  const [showFixtures, setShowFixtures] = useState<boolean>(false)
  const [showFurniture, setShowFurniture] = useState<boolean>(false)
  const [addGardensAndRoofSlopes, setAddGardensAndRoofSlopes] = useState<boolean>(false)

  const [selectedStyle, setSelectedStyle] = useState<Styles>(Styles.STYLE_1)
  const [selectedOrientation, setSelectedOrientation] = useState<Orientations>(Orientations.BIRD_VIEW)

  // LAYOUT
  const [showRoomNames, setShowRoomNames] = useState<boolean>(false)
  const [showScalebar, setShowScalebar] = useState<boolean>(false)
  const [showCompass, setShowCompass] = useState<boolean>(false)
  const [isActiveDimensions, setIsActiveDimensions] = useState<boolean>(false)
  const [showDimensionChain, setShowDimensionChain] = useState<boolean>(true)
  const [showRoomArea, setShowRoomArea] = useState<boolean>(true)
  const [outputLanguage, setOutputLanguage] = useState<Language>(Language.EN)
  const [fileOrientation, setFileOrientation] = useState<Set<FloorPlanFileOrientation>>(new Set([FloorPlanFileOrientation.HORIZONTAL]))
  const [floorPlanFormat, setFloorPlanFormat] = useState<Record<string, FloorPlanFormat>>({ [FloorPlanFormat.JPG]: FloorPlanFormat.JPG })
  const [isActiveLogo, setIsActiveLogo] = useState<boolean>(false)
  const [companyLogo, setCompanyLogo] = useState<Nullable<string>>(null)
  const [showDisclaimer, setShowDisclaimer] = useState<boolean>(false)
  const [disclaimerText, setDisclaimerText] = useState<string>('')
  const [isMultiStoryPlan, setIsMultiStoryPlan] = useState<boolean>(false)

  // MISC
  const [comment, setComment] = useState('')

  // TEMPLATES
  const [selectedTemplateId, setSelectedTemplateId] = useState<Nullable<string>>(null)
  const [floorPlanTemplateType, setFloorPlanTemplateType] = useState<Nullable<FloorPlanSelectionType>>(null)

  const is3DRenderType = useMemo(() => {
    if (!is3DAllowed) return false
    return hasFloorPlanProduct && currentFloorPlanRenderType === FloorPlanRenderType.RENDER_3D
  }, [hasFloorPlanProduct, currentFloorPlanRenderType, is3DAllowed])

  // DATA
  const getFloorPlanRequestData = useCallback<() => FloorPlanConfigDTO>(() => {
    return {
      // Has to be updated when 3D option is available for the new Floor Plan configuration design
      style: is3DRenderType ? selectedStyle : selectedColor,
      orientation: is3DRenderType ? selectedOrientation : undefined,
      fixtures: showFixtures,
      furniture: showFurniture,
      roomNames: showRoomNames,
      fileOrientation: is3DRenderType ? [] : Array.from(fileOrientation),
      isMultiStoryPlan,
      gardensAndRoofSlopes: addGardensAndRoofSlopes,
      scaleBar: showScalebar,
      compass: showCompass,
      companyLogo,
      clientComment: comment,
      outputFileFormat: Object.keys(floorPlanFormat),
      outputFileLanguage: outputLanguage,
      dimensions: {
        showDimensionChain: isActiveDimensions ? showDimensionChain : false,
        showEachRoomArea: isActiveDimensions ? showRoomArea : false,
      },
      disclaimer: showDisclaimer ? disclaimerText : undefined,
      // A blank comment goes out as null, as in the v2 serializer, so whitespace-only notes are never stored or shown
      floorPlanProductConfig: floorPlanProductSettings.map((settings) => ({
        ...settings,
        drawOnlySpecificUnitComment: settings.drawOnlySpecificUnitComment?.trim() ? settings.drawOnlySpecificUnitComment : null,
      })).toObject(),
    }
  }, [addGardensAndRoofSlopes, comment, companyLogo, disclaimerText, fileOrientation, floorPlanFormat, floorPlanProductSettings, is3DRenderType, isActiveDimensions, isMultiStoryPlan, outputLanguage, selectedColor, selectedOrientation, selectedStyle, showCompass, showDimensionChain, showDisclaimer, showFixtures, showFurniture, showRoomArea, showRoomNames, showScalebar])

  const getSelectedOptionsAnalytics = useCallback(() => {
    const options = []

    if (is3DRenderType) options.push(selectedOrientation)
    if (showFixtures) options.push('fixtures')
    if (showFurniture) options.push('furniture')
    if (showRoomNames) options.push('roomNames')
    if (showScalebar) options.push('scalebar')
    if (showCompass) options.push('compass')
    if (showDimensionChain) options.push('dimensionChain')
    if (showRoomArea) options.push('roomArea')
    if (isActiveLogo) options.push('logo')
    if (selectedPropertyType) options.push(selectedPropertyType)
    if (showDisclaimer && disclaimerText) options.push('disclaimer')

    options.push(...Object.keys(floorPlanFormat))
    options.push(outputLanguage)

    return options
  }, [is3DRenderType, showFixtures, showFurniture, showRoomNames, showScalebar, showCompass, showDimensionChain, showRoomArea, isActiveLogo, selectedPropertyType, showDisclaimer, disclaimerText, floorPlanFormat, outputLanguage, selectedOrientation])

  const resetConfiguration = useCallback(() => {
    setSelectedTemplateId(null)
    setAddGardensAndRoofSlopes(false)
    setSelectedColor(FloorPlanColors.BLACK)
    setSelectedPropertyType(null)
    setShowFixtures(false)
    setShowFurniture(false)
    setSelectedStyle(Styles.STYLE_1)
    setSelectedOrientation(Orientations.BIRD_VIEW)
    setShowRoomNames(false)
    setShowScalebar(false)
    setShowCompass(false)
    setIsActiveDimensions(false)
    setShowDimensionChain(false)
    setShowRoomArea(false)
    setOutputLanguage(Language[(targetUser.language as Language)] ?? Language.EN)
    setFloorPlanFormat({ [FloorPlanFormat.JPG]: FloorPlanFormat.JPG })
    setIsActiveLogo(false)
    setFileOrientation(new Set([FloorPlanFileOrientation.HORIZONTAL]))
    setIsMultiStoryPlan(false)
    setCompanyLogo(null)
    setShowDisclaimer(false)
    setDisclaimerText('')
    setComment('')
  }, [setSelectedPropertyType, targetUser.language])

  const allSelectedFloorPlanProducts = useMemo(() => {
    // Is there some FP product in the main product selection
    const mainProducts = Object.values(selectedProducts).filter((product) => ProductKindsFloorPlan.has(product.kind))

    // Or as option of the main product then return main product
    const optionalProductsToMainProduct = Object.entries(selectedProductOptions)
      .filter(([, optionProducts]) => Object.values(optionProducts).some((product) => ProductKindsFloorPlan.has(product.kind)))
      .map(([mainProductId]) => selectedProducts[parseInt(mainProductId)])

    // Return only main products even they are not FP
    return [...mainProducts, ...optionalProductsToMainProduct]
  }, [selectedProducts, selectedProductOptions])

  /**
   * Sync the 3D floor-plan add-on with the current render type, for each selected main product.
   *
   * For every main product:
   * 1) Resolve its parent-scoped 3D extra (catch-all fallback). Must be per-parent — each size
   *    has its own extra (ISSUE-1468).
   * 2) Attach it when render type is 3D and it is not already selected.
   * 3) Detach it when render type is not 3D.
   *
   * Loop-safe: FLOOR_PLAN_EDITING_3D is not in ProductKindsFloorPlan, so toggling the add-on
   * does not change allSelectedFloorPlanProducts; the isSelected guard prevents redundant calls.
   */
  useEffect(() => {
    if (!allSelectedFloorPlanProducts?.length) return

    const extra3DProducts = extraCatalogueProducts(ProductKind.FLOOR_PLAN_EDITING_3D)

    for (const { id } of allSelectedFloorPlanProducts) {

      const extra3DProduct = findExtraProductByParent(extra3DProducts, id)
      if (!extra3DProduct) continue

      const optionProduct = toCatalogueOptionProduct(extra3DProduct)
      const isSelected = !!selectedProductOptions?.[id]?.[optionProduct.id]

      if (currentFloorPlanRenderType === FloorPlanRenderType.RENDER_3D && !isSelected) {
        selectOptionProduct(id, optionProduct, 1)
      }

      if (currentFloorPlanRenderType !== FloorPlanRenderType.RENDER_3D && isSelected) {
        unselectOptionProduct(id, optionProduct.id)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFloorPlanRenderType, allSelectedFloorPlanProducts])

  return {
    is3DRenderType,
    currentFloorPlanProductType,
    currentFloorPlanRenderType,
    selectedColor,
    selectedStyle,
    selectedOrientation,
    selectedPropertyType,
    showFixtures,
    showFurniture,
    showRoomNames,
    showScalebar,
    showCompass,
    isActiveDimensions,
    isActiveLogo,
    companyLogo,
    showDimensionChain,
    showRoomArea,
    outputLanguage,
    floorPlanFormat,
    floorPlanTemplateType,
    selectedTemplateId,
    showDisclaimer,
    is3DAllowed,
    addGardensAndRoofSlopes,
    isMultiStoryPlan,
    fileOrientation,
    floorPlanProductSettings,
    comment,
    setComment,
    setFloorPlanProductSettings,
    setFileOrientation,
    setIsMultiStoryPlan,
    setAddGardensAndRoofSlopes,
    setSelectedTemplateId,
    setFloorPlanTemplateType,
    setCurrentFloorPlanRenderType,
    setSelectedColor,
    setSelectedStyle,
    setSelectedOrientation,
    setSelectedPropertyType,
    setShowFixtures,
    setShowFurniture,
    setShowRoomNames,
    setShowScalebar,
    setShowCompass,
    setOutputLanguage,
    setFloorPlanFormat,
    setIsActiveDimensions,
    setIsActiveLogo,
    setCompanyLogo,
    setShowDimensionChain,
    setShowRoomArea,
    getFloorPlanRequestData,
    resetConfiguration,
    getSelectedOptionsAnalytics,
    setShowDisclaimer,
    setDisclaimerText,
    disclaimerText,
  }
})
